<template>
  <div class="user-status-filter-item sensor">
    <label
      class="item-label"
      v-t.preserve="'appUserStatusFilterSensor.sensor'"
    />
    <div class="action-container">
      <b-form-select
        size="sm"
        v-model="localValue.sensorItemId"
        v-bind:options="sensorItemOptions"
      />
      <div v-if="localValue.sensorItemId" class="criteria-container">
        <label class="label">
          <b-form-checkbox size="md" v-model="localValue.canSetValue" />
          <span
            class="text"
            v-t.preserve="'appUserStatusFilterSensor.message.specifyValue'"
          />
        </label>
        <!-- カテゴリ値の場合 -->
        <div v-if="isSensorItemCategorical" class="forms">
          <b-form-select
            size="sm"
            v-bind:disabled="!localValue.canSetValue"
            v-model="localValue.sensorValue"
            v-bind:options="sensorValueOptions"
          >
            <template v-slot:first>
              <b-form-select-option
                v-bind:value="null"
                disabled
                v-t.preserve="'appUserStatusFilterSensor.placeholder.select'"
              />
            </template>
          </b-form-select>
        </div>
        <!-- 連続値の場合 -->
        <div v-else class="forms">
          <b-form-input
            lazy
            number
            size="sm"
            type="number"
            v-bind:disabled="!localValue.canSetValue"
            v-bind:placeholder="$t('appUserStatusFilterSensor.example.value')"
            v-model="inputValue"
            v-on:blur="$_applyToSensorValue"
            v-on:keydown.enter="$_applyToSensorValue"
            class="value-form"
          />
          <span class="text unit">
            {{ currentSensorItem?.unit || '' }}
          </span>
          <b-form-radio-group
            v-bind:disabled="!localValue.canSetValue"
            v-model="localValue.sensorValueOperator"
            class="operator-selector"
          >
            <label class="label">
              <b-form-radio v-bind:value="SENSOR_VALUE_OPERATOR.LESS" />
              <span
                class="text"
                v-t.preserve="'appUserStatusFilterSensor.operator.less'"
              />
            </label>
            <label class="label">
              <b-form-radio v-bind:value="SENSOR_VALUE_OPERATOR.GREATER" />
              <span
                class="text"
                v-t.preserve="'appUserStatusFilterSensor.operator.greater'"
              />
            </label>
          </b-form-radio-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { SENSOR_VALUE_OPERATOR } from '@/composables/member/UseUserStatusFilterCondition'
import { mapGetters } from 'vuex'

export default {
  name: 'AppUserStatusFilterSensor',
  props: {
    /**
     * センサー測定項目ID
     */
    sensorItemId: {
      type: Number,
      default: null,
    },
    /**
     * センサー値を指定可能な場合はtrue
     */
    canSetValue: {
      type: Boolean,
      default: false,
    },
    /**
     * センサー値(閾値)
     */
    sensorValue: {
      type: Number,
      default: null,
    },
    /**
     * センサー値の比較演算子
     * @values 'eq', 'ge', 'le'
     */
    sensorValueOperator: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    return {
      handleApiError,
      SENSOR_VALUE_OPERATOR,
    }
  },
  data: function () {
    return {
      // ローカル値
      localValue: {
        sensorItemId: null,
        canSetValue: false,
        sensorValue: null,
        sensorValueOperator: null,
      },
      // 値フォームの v-model 用値
      inputValue: null,
    }
  },
  computed: {
    ...mapGetters('sensorItem', ['sensorItems', 'sensorItemMap']),
    /**
     * センサー測定項目の選択肢
     * @type {Array.<Object>
     */
    sensorItemOptions: function () {
      const options = this.sensorItems.map((item) => ({
        value: item.id,
        text: item.sensor_item_name,
      }))
      options.unshift({
        value: null,
        text: this.$t('appUserStatusFilterSensor.allSensorItems'),
      })
      return options
    },
    /**
     * 選択しているセンサー測定項目データ
     * @type {Object}
     */
    currentSensorItem: function () {
      return this.sensorItemMap.get(this.localValue.sensorItemId)
    },
    /**
     * 選択されたセンサー測定項目がカテゴリ値の場合はtrue
     * @type {Boolean}
     */
    isSensorItemCategorical: function () {
      return (
        Array.isArray(this.currentSensorItem?.value_type) &&
        this.currentSensorItem.value_type.length
      )
    },
    /**
     * センサー値の選択肢
     * @type {Array.<Object>}
     */
    sensorValueOptions: function () {
      return this.currentSensorItem?.value_type || []
    },
    /**
     * 条件基準部テキスト
     * @type {String}
     */
    criteriaText: function () {
      const {
        canSetValue,
        sensorValue: value,
        sensorValueOperator: operator,
      } = this.localValue

      if (!canSetValue || value == null) return ''
      if (this.isSensorItemCategorical) {
        return this.sensorValueOptions.find((o) => o.value === value)?.text
      }

      const i18nParam = {
        value,
        unit: this.currentSensorItem?.unit,
      }
      if (operator === SENSOR_VALUE_OPERATOR.GREATER) {
        return this.$t(
          'appUserStatusFilterSensor.message.valueCriteria.greater',
          i18nParam
        )
      } else if (operator === SENSOR_VALUE_OPERATOR.LESS) {
        return this.$t(
          'appUserStatusFilterSensor.message.valueCriteria.less',
          i18nParam
        )
      } else {
        return ''
      }
    },
    /**
     * イベント返却用のテキスト
     * @type {String}
     */
    resultText: function () {
      if (!this.localValue.sensorItemId) return ''

      const sensorItemName =
        this.sensorItemMap.get(this.localValue.sensorItemId)
          ?.sensor_item_name || ''
      return this.criteriaText
        ? `${sensorItemName}(${this.criteriaText})`
        : sensorItemName
    },
  },
  watch: {
    sensorItemId: {
      immediate: true,
      handler: function (value) {
        this.localValue.sensorItemId = value
      },
    },
    canSetValue: {
      immediate: true,
      handler: function (value) {
        this.localValue.canSetValue = value
      },
    },
    sensorValue: {
      immediate: true,
      handler: function (value) {
        this.localValue.sensorValue = value
      },
    },
    sensorValueOperator: {
      immediate: true,
      handler: function (value) {
        this.localValue.sensorValueOperator = value
      },
    },
    isSensorItemCategorical: function (value) {
      // カテゴリ値の場合は一致で固定 連続値の場合は以上を初期値として設定
      this.localValue.sensorValueOperator = value
        ? SENSOR_VALUE_OPERATOR.EQUAL
        : SENSOR_VALUE_OPERATOR.GREATER
    },
    'localValue.sensorItemId': function () {
      // 測定項目変更時は閾値をクリアする
      this.localValue.sensorValue = null
    },
    'localValue.sensorValue': {
      immediate: true,
      handler: function (value) {
        this.inputValue = value
      },
    },
    localValue: {
      deep: true,
      handler: function () {
        this.$_onChange()
      },
    },
  },
  created: function () {
    this.$_getSensorItems()
  },
  mounted: function () {
    // 初回結果表示のためにpropsの値で変更されたことにする
    if (this.sensorItems.length) {
      this.$_onChange()
    } else {
      // 取得できるのを待つ(1回だけwatch)
      const unwatch = this.$watch('sensorItems', () => {
        this.$_onChange()
        unwatch()
      })
    }
  },
  methods: {
    /**
     * センサー測定項目データを取得する
     */
    $_getSensorItems: async function () {
      try {
        await this.$store.dispatch('sensorItem/updateSensorItemsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch sensorItems. use fetched before.')
        )
      }
    },

    /**
     * センサー値のフォーム入力値をローカル値に反映
     */
    $_applyToSensorValue: function () {
      this.localValue.sensorValue =
        this.inputValue === '' ? null : this.inputValue
    },

    /**
     * 変更時処理
     */
    $_onChange: function () {
      /**
       * 選択値変更イベント
       * @param {Object} value 選択値
       * @param {String} text 選択値表示テキスト
       */
      this.$emit('changed', { value: this.localValue, text: this.resultText })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../assets/scss/parts/user/user-status-filter-item';

.criteria-container {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;
  flex-wrap: wrap;

  .label {
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    margin: 0;
  }

  .forms {
    flex-grow: 1;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: nowrap;
  }

  .text {
    font-size: $font-size-small;
    user-select: none;
    white-space: nowrap;
    text-align: left;

    &.unit {
      margin-left: 4px;
    }
  }

  .value-form {
    width: 4rem;

    // スピンボタンを消す
    appearance: textfield;
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .operator-selector {
    width: auto;
    margin-left: 8px;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    column-gap: 8px;

    .label {
      display: inline-flex;
      justify-content: flex-start;
      align-items: center;
      margin: 0;
    }

    ::v-deep .custom-control {
      // テキストやフォームをラジオボタンに少し近づけるため右側 margin を調整
      margin: 0 -2px 0 0;
    }

    ::v-deep .custom-control-input {
      // disabled 時のスタイル => 全く選択されていないように見せる
      &:disabled ~ .custom-control-label {
        &::before {
          border-color: #adb5bd;
          background-color: #e9ecef;
        }
        &::after {
          display: none;
        }
      }

      // 選択時のスタイル => 色を緑系にする
      &:not(:disabled):checked ~ .custom-control-label::before {
        border-color: $entity-color-green;
        background-color: $entity-color-green;
      }
      // focus時のshadowは出さない
      &:focus ~ .custom-control-label::before {
        box-shadow: none;
      }
    }
  }
}
</style>
